import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

const MAX_PER_VARIANT_MESSAGE = 'Você só pode ter uma unidade de cada curso no carrinho.';
const MATRICULA_ATTRIBUTE_IDENTIFIER_PROPERTY = '_matricula_attribute_identifier';
const MATRICULA_CART_ATTRIBUTE_SUFFIXES = ['Nome', 'Estado', 'Cidade', 'Polo', 'ID do Polo'];
const MATRICULA_CART_ATTRIBUTE_PATTERN = /^Curso .+ - (?:Nome|Estado|Cidade|Polo|ID do Polo)$/;

function getCartItemMatriculaAttributeIdentifier(item) {
  const properties = item && item.properties && typeof item.properties === 'object' ? item.properties : {};
  const identifier = properties[MATRICULA_ATTRIBUTE_IDENTIFIER_PROPERTY];
  return identifier === null || typeof identifier === 'undefined' ? '' : String(identifier).trim();
}

function getMatriculaCartAttributeKeysToClear(cart, removedIdentifier) {
  const items = Array.isArray(cart && cart.items) ? cart.items : [];
  const attributes = cart && cart.attributes && typeof cart.attributes === 'object' ? cart.attributes : {};

  if (!items.length) {
    return Object.keys(attributes).filter((key) => MATRICULA_CART_ATTRIBUTE_PATTERN.test(key));
  }

  const identifier = removedIdentifier === null || typeof removedIdentifier === 'undefined'
    ? ''
    : String(removedIdentifier).trim();
  if (!identifier || items.some((item) => getCartItemMatriculaAttributeIdentifier(item) === identifier)) return [];

  return MATRICULA_CART_ATTRIBUTE_SUFFIXES
    .map((suffix) => `Curso ${identifier} - ${suffix}`)
    .filter((key) => Object.prototype.hasOwnProperty.call(attributes, key));
}

async function fetchCartStateForMatriculaCleanup() {
  const cartUrl = Theme.routes.cart_url || '/cart';
  const cartEndpoint = cartUrl.endsWith('.js') ? cartUrl : `${cartUrl}.js`;
  const response = await fetch(cartEndpoint, { credentials: 'same-origin', cache: 'no-store' });

  if (!response.ok) {
    throw new Error(`Falha ao ler o carrinho antes da remoção (${response.status})`);
  }

  return response.json();
}

async function clearRemovedMatriculaCartAttributes(cart, removedIdentifier) {
  const keysToClear = getMatriculaCartAttributeKeysToClear(cart, removedIdentifier);
  if (!keysToClear.length) return;

  const attributes = Object.fromEntries(keysToClear.map((key) => [key, '']));
  const cartUpdateUrl = Theme.routes.cart_update_url || '/cart/update.js';
  const response = await fetch(cartUpdateUrl, fetchConfig('json', {
    body: JSON.stringify({ attributes }),
  }));

  if (!response.ok) {
    throw new Error(`Falha ao limpar atributos da matrícula removida (${response.status})`);
  }
}

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity > 1) {
      this.#showQuantityLimitMessage(line);
      return;
    }

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  async updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    let removedMatriculaIdentifier = '';
    if (quantity === 0) {
      try {
        const cartBeforeRemoval = await fetchCartStateForMatriculaCleanup();
        removedMatriculaIdentifier = getCartItemMatriculaAttributeIdentifier(cartBeforeRemoval.items?.[line - 1]);
      } catch (error) {
        console.warn('Não foi possível identificar a matrícula antes da remoção:', error);
      }
    }

    try {
      const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
      const responseText = await response.text();
      const parsedResponseText = JSON.parse(responseText);

      resetShimmer(this);

      if (parsedResponseText.errors) {
        this.#handleCartError(line, parsedResponseText);
        return;
      }

      if (quantity === 0) {
        try {
          localStorage.removeItem('checkoutDirectUrl');
        } catch (error) {
          console.warn('Não foi possível invalidar a URL direta de checkout:', error);
        }

        try {
          await clearRemovedMatriculaCartAttributes(parsedResponseText, removedMatriculaIdentifier);
        } catch (error) {
          console.warn('Não foi possível limpar os atributos da matrícula removida:', error);
        }
      }

      const newSectionHTML = new DOMParser().parseFromString(
        parsedResponseText.sections[this.sectionId],
        'text/html'
      );

      // Grab the new cart item count from a hidden element
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      this.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: parsedResponseText.sections,
        })
      );

      morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
    } catch (error) {
      console.error(error);
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  #showQuantityLimitMessage(line) {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (quantityInput) {
      quantityInput.value = '1';
    }

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement) || !(cartItemErrorContainer instanceof HTMLElement)) {
      return;
    }

    cartItemError.textContent = MAX_PER_VARIANT_MESSAGE;
    cartItemErrorContainer.classList.remove('hidden');

    setTimeout(() => {
      cartItemErrorContainer.classList.add('hidden');
    }, 4000);
  }

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
